import { MutationTree } from 'vuex';
import { Pricing } from '@/app/domain/Pricing';
import { Setting } from '@/app/domain/Setting';
import { Company } from '@/app/domain/Company';
import { Contact } from '@/app/domain/Contact';
import { EmailNotificationsSettings } from '@/app/domain/EmailNotificationsSettings';
import { ConsumerProfile } from '@/app/domain/ConsumerProfile';
import { ModuleState } from './state';
import { UserPermissions } from '../services/UserPermissionsService';
import { DeleteAccountConfirmationStatus, CustomerType, TenantLicense } from '../services/DeleteAccountService';

export function createMutations(): MutationTree<ModuleState> {
  return {
    setIsWizardFetching(state: ModuleState, isWizardFetching: boolean): void {
      state.isWizardFetching = isWizardFetching;
    },
    setIsWizardAvailable(state: ModuleState, isWizardAvailable: boolean): void {
      state.isWizardAvailable = isWizardAvailable;
    },
    setPricing(state: ModuleState, pricing: Pricing | null): void {
      state.pricing = pricing;
    },
    setIsPricingFetching(state: ModuleState, isPricingFetching: boolean): void {
      state.isPricingFetching = isPricingFetching;
    },
    setSetting(state: ModuleState, setting: Setting | null): void {
      state.setting = setting;
    },
    setIsSettingFetching(state: ModuleState, isSettingFetching: boolean): void {
      state.isSettingFetching = isSettingFetching;
    },
    setIsProfileFetching(state: ModuleState, isProfileFetching: boolean): void {
      state.isProfileFetching = isProfileFetching;
    },
    setSelfServiceMode(state: ModuleState, selfServiceMode: boolean): void {
      state.selfServiceMode = selfServiceMode;
    },
    setIsSelfServiceModeFetching(state: ModuleState, isSelfServiceModeFetching: boolean): void {
      state.isSelfServiceModeFetching = isSelfServiceModeFetching;
    },
    setCompany(state: ModuleState, company: Company): void {
      state.company = company;
    },
    setIsCompanyFetching(state: ModuleState, isCompanyFetching: boolean): void {
      state.isCompanyFetching = isCompanyFetching;
    },
    setIsCompanyEditing(state: ModuleState, isCompanyEditing: boolean): void {
      state.isCompanyEditing = isCompanyEditing;
    },
    setContactList(state: ModuleState, contactList: Contact[]): void {
      state.contactList = contactList;
    },
    setAddedContactList(state: ModuleState, contactList: Contact[]): void {
      state.addedContactList = contactList;
    },
    setNotAddedContactList(state: ModuleState, contactList: Contact[]): void {
      state.notAddedContactList = contactList;
    },
    setIsContactListFetching(state: ModuleState, isContactListFetching: boolean): void {
      state.isContactListFetching = isContactListFetching;
    },
    setIsTenantContactListFetching(state: ModuleState, isTenantContactListFetching: boolean): void {
      state.isTenantContactListFetching = isTenantContactListFetching;
    },
    setTenantContactList(state: ModuleState, contactList: Contact[]): void {
      state.tenantContactList = contactList;
    },
    setIsDirectPartnerFetching(state: ModuleState, isDirectPartnerFetching: boolean): void {
      state.isDirectPartnerFetching = isDirectPartnerFetching;
    },
    setIsDirectPartner(state: ModuleState, isDirectPartner: boolean): void {
      state.isDirectPartner = isDirectPartner;
    },
    setIsContactCreating(state: ModuleState, isContactCreating: boolean): void {
      state.isContactCreating = isContactCreating;
    },
    setIsContactEditing(state: ModuleState, isContactEditing: boolean): void {
      state.isContactEditing = isContactEditing;
    },
    setIsContactDeleting(state: ModuleState, isContactDeleting: boolean): void {
      state.isContactDeleting = isContactDeleting;
    },
    setIsVerificationEmailSending(state: ModuleState, isVerificationEmailSending: boolean): void {
      state.isVerificationEmailSending = isVerificationEmailSending;
    },
    setConsumerProfile(state: ModuleState, consumerProfile: ConsumerProfile): void {
      state.consumerProfile = consumerProfile;
    },
    setIsConsumerProfileFetching(state: ModuleState, isConsumerProfileFetching: boolean): void {
      state.isConsumerProfileFetching = isConsumerProfileFetching;
    },
    setIsConsumerProfileEditing(state: ModuleState, isConsumerProfileEditing: boolean): void {
      state.isConsumerProfileEditing = isConsumerProfileEditing;
    },
    setEmailNotificationsSettings(state: ModuleState, emailNotificationsSettings: EmailNotificationsSettings): void {
      state.emailNotificationsSettings = emailNotificationsSettings;
    },
    setIsEmailNotificationsSettingsFetching(state: ModuleState, isEmailNotificationsSettingsFetching: boolean): void {
      state.isEmailNotificationsSettingsFetching = isEmailNotificationsSettingsFetching;
    },
    setIsEmailNotificationsSettingsEditing(state: ModuleState, isEmailNotificationsSettingsEditing: boolean): void {
      state.isEmailNotificationsSettingsEditing = isEmailNotificationsSettingsEditing;
    },
    setUserPermissions(state: ModuleState, userPermissions: UserPermissions): void {
      state.userPermissions = userPermissions;
    },
    setIsTenantLicensesFetching(state: ModuleState, isTenantLicensesFetching: boolean): void {
      state.isTenantLicensesFetching = isTenantLicensesFetching;
    },
    setTenantLicenses(state: ModuleState, tenantLicenses: TenantLicense[]): void {
      state.tenantLicenses = tenantLicenses;
    },
    setIsTenantStorageUsageFetching(state: ModuleState, isTenantStorageUsageFetching: boolean): void {
      state.isTenantStorageUsageFetching = isTenantStorageUsageFetching;
    },
    setTenantStorageUsage(state: ModuleState, tenantStorageUsage: number): void {
      state.tenantStorageUsage = tenantStorageUsage;
    },
    setIsDeleteAccountConfirmationStatusFetching(
      state: ModuleState, isDeleteAccountConfirmationStatusFetching: boolean
    ): void {
      state.isDeleteAccountConfirmationStatusFetching = isDeleteAccountConfirmationStatusFetching;
    },
    setDeleteAccountConfirmationStatus(
      state: ModuleState, deleteAccountConfirmationStatus: keyof typeof DeleteAccountConfirmationStatus
    ): void {
      state.deleteAccountConfirmationStatus = deleteAccountConfirmationStatus;
    },
    setCustomerType(state: ModuleState, customerType: keyof typeof CustomerType): void {
      state.customerType = customerType;
    }
  };
}
